"use client";

import {
  Clock5,
  Equal,
  Eye,
  Menu,
  CheckCircle,
  BadgeCheck,
} from "lucide-react";
import Link from "next/link";
import React, { Suspense, useState, useEffect } from "react"; // Import useEffect
import Markdown from "react-markdown";
import { formatDistanceStrict } from "date-fns";
import { Skeleton } from "./ui/skeleton";
import { formatNumber } from "@/lib/utils";
import Image from "next/image";
import { Vibrant } from "node-vibrant/browser";
import { useTheme } from "next-themes";
import { notoSerif } from "@/lib/fonts";

const hexToRgba = (hex: string, alpha: number) => {
  const normalized = hex.replace("#", "");
  if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) return `rgba(82, 39, 255, ${alpha})`;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const EpisodeCard = ({
  episode,
  className,
}: {
  episode: {
    id: string;
    title: string | null;
    description: string | null;
    cover: string;
    user?: {
      id?: string;
      name: string | null | undefined;
      image?: string | null;
      subscriptionStatus?: string | null; // Changed from isPro
    };
    views?: number | null;
    date?: Date | null;
  };
  className?: string;
}) => {
  const { resolvedTheme } = useTheme();
  const [imageLoading, setImageLoading] = useState(true); // State to track image loading
  const [imageError, setImageError] = useState(false); // State to track image error
  const [majorColorHex, setMajorColorHex] = useState<string | null>(null);
  const [formattedDate, setFormattedDate] = useState<string>(
    episode.date ? "Just now" : "Unknown date"
  );
  const [isHydrated, setIsHydrated] = useState(false);

  // Update date string only after hydration is complete
  useEffect(() => {
    setIsHydrated(true);

    if (episode.date) {
      // Set initial formatted date
      setFormattedDate(
        formatDistanceStrict(episode.date, new Date(), { addSuffix: true })
      );

      // Optional: Set up an interval to update the date periodically
      const intervalId = setInterval(() => {
        setFormattedDate(
          formatDistanceStrict(episode.date!, new Date(), { addSuffix: true })
        );
      }, 60000); // Update every minute

      return () => clearInterval(intervalId);
    }
  }, [episode.date]);

  useEffect(() => {
    if (!episode.cover) {
      setMajorColorHex(null);
      return;
    }

    let cancelled = false;
    setMajorColorHex(null);

    Vibrant.from(`https://wsrv.nl/?url=${encodeURIComponent(episode.cover)}`)
      .getPalette()
      .then((palette) => {
        if (cancelled) return;
        const swatches = Object.values(palette).filter(
          (swatch): swatch is NonNullable<(typeof palette)[keyof typeof palette]> =>
            Boolean(swatch)
        );
        const majorSwatch = swatches.sort(
          (a, b) => b.population - a.population
        )[0];
        const color =
          majorSwatch?.hex ||
          palette.Vibrant?.hex ||
          palette.LightVibrant?.hex ||
          palette.Muted?.hex ||
          null;
        setMajorColorHex(color);
      })
      .catch(() => {
        if (cancelled) return;
        setMajorColorHex(null);
      });

    return () => {
      cancelled = true;
    };
  }, [episode.cover]);

  const imageWidth = 288;
  const imageHeight = 192;
  const multiplier = 4;
  const wsrvnlUrl = episode.cover
    ? `https://${imageError ? "wsrv.nl" : "wsrv.nl"}/?url=${encodeURIComponent(episode.cover)}&w=${multiplier * imageWidth}&h=${multiplier * imageHeight}&format=webp`
    : "";
  const showCardGradient = !imageLoading && Boolean(majorColorHex);
  const isDarkTheme = resolvedTheme === "dark";
  const cardGradientStyle =
    showCardGradient && majorColorHex
      ? {
          backgroundImage: isDarkTheme
            ? `linear-gradient(180deg, ${hexToRgba(majorColorHex, 0.5)} 0%, ${hexToRgba(majorColorHex, 0.2)} 34%, ${hexToRgba(majorColorHex, 0.08)} 58%, hsl(var(--background)) 100%)`
            : `linear-gradient(180deg, ${hexToRgba(majorColorHex, 0.11)} 0%, ${hexToRgba(majorColorHex, 0.05)} 16%, ${hexToRgba(majorColorHex, 0.02)} 28%, ${hexToRgba(majorColorHex, 0)} 40%)`,
        }
      : undefined;

  return (
    <div
      key={episode.id}
      className={`flex flex-col select-none rounded-2xl border relative group hover:cursor-pointer h-[24.5rem] transition duration-300 ease-in-out active:scale-[0.99] hover:shadow-xl hover:shadow-accent/50 shadow-xs ${className} ${isDarkTheme ? "bg-background/50" : "bg-background"} overflow-hidden`}
      style={cardGradientStyle}
    >
      <Link
        href={`/bite/${episode.id}`}
        draggable={false}
        className="focus-visible:outline-none overflow-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background flex flex-col h-full transition duration-300 ease-in-out"
      >
        {episode.cover ? (
          <div className="p-2 pb-0">
            <div
              className="relative w-full overflow-hidden rounded-xl aspect-[3/2] max-h-48 shadow-sm"
            >
              {imageLoading && ( // Conditionally render Skeleton based on loading state
                <Skeleton className="absolute inset-0 w-full h-full rounded-xl" />
              )}
              <img
                src={wsrvnlUrl}
                alt={episode.title || "Episode cover"} // Add alt text
                className={`w-full h-full object-cover rounded-xl transition-all duration-300 delay-50 ${imageLoading ? "opacity-0" : "opacity-100"} group-hover:scale-[1.03]`}
                draggable="false"
                loading="eager" // Use eager loading for better performance
                // quality={100}
                width={imageWidth}
                height={imageHeight}
                onLoad={() => setImageLoading(false)} // Set loading state to false when image loads
                onError={() => {
                  setImageLoading(false); // Also handle potential loading errors
                  setImageError(true); // Set error state to true to add &fit=cover
                }}
              />
            </div>
          </div>
        ) : (
          <div className="p-2 pb-0 rounded-t-xl">
            <div className="w-full object-cover aspect-[3/2] rounded-xl max-h-48 ring-1 ring-border/70 bg-gradient-to-bl from-primary/80 to-primary/50" />
          </div>
        )}

        <div className="flex flex-col p-4 gap-1">
          <h2
            className={`font-medium line-clamp-2 leading-snug ${notoSerif.className}`}
          >
            {episode.title}
          </h2>
          <div className="text-muted-foreground text-sm line-clamp-3">
            <Markdown>{episode.description}</Markdown>
          </div>
        </div>
      </Link>

      {episode.user && (
        <div className="bottom-4 absolute w-full px-4">
          <div className="flex flex-row justify-between items-end">
            <div className="inline-flex gap-1.5 items-center max-w-[60%]">
              {episode.user.image && (
                <img
                  src={episode.user.image}
                  alt={episode.user.name![0].toUpperCase()}
                  className="size-4 rounded-full border shadow-sm"
                />
              )}
              <Link
                href={`/user/${episode.user.id}`}
                className="text-muted-foreground font-medium text-xs truncate hover:text-primary hover:underline inline-flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                {episode.user.name}
                {episode.user.subscriptionStatus === "active" && (
                  <BadgeCheck className="size-3.5 text-primary" />
                )}
              </Link>
            </div>
            <div className="flex flex-col gap-1 items-end text-right">
              <div className="inline-flex gap-1.5 items-center">
                <Clock5 className="text-muted-foreground size-3.5" />
                <p className="text-xs text-muted-foreground whitespace-nowrap">
                  {formattedDate}
                </p>
              </div>
              {(episode.views || 0) > 1000 && (
                <div className="inline-flex gap-1.5 items-center">
                  <Eye className="text-muted-foreground size-3.5" />
                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatNumber(episode.views!)} views
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EpisodeCard;
